import Link from "next/link";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { ParagraphTextMiniCards as ParagraphTextMiniCardsData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "./ParagraphTextMiniCards.module.css";

/**
 * Layout 46 - `PARAGRAPH_TEXT_MINI_CARDS_LAYOUT`: text + mini cards.
 *
 * Struktura:
 *   - Light full-bleed mint bg
 *   - 2-kol grid:
 *       Lewa: eyebrow (text_1) + H2 (name) + lead (text, HTML) + solid teal CTA
 *       Prawa: grid 2x2 mini-kart (icon + name, opcjonalnie klikalna gdy href)
 *   - Bez opisu na kartach (kompaktowo)
 *   - Puste karty (bez name) filtrowane po backendzie
 */
export function ParagraphTextMiniCards({
  paragraph,
  t: _t,
}: {
  paragraph: ParagraphTextMiniCardsData;
  t: ParagraphTranslations;
}) {
  const {
    text_1: eyebrow,
    name: title,
    text: lead,
    button_name: ctaLabel,
    button_url: ctaUrl,
    items,
  } = paragraph;

  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasLead = lead && lead.trim() !== "";
  const hasCta = ctaLabel && ctaLabel.trim() !== "" && ctaUrl && ctaUrl.trim() !== "";
  const hasItems = Array.isArray(items) && items.length > 0;

  const ctaHref = hasCta ? extractInternalHref(ctaUrl) : null;

  return (
    <section className={styles.section}>
      <div className={styles.row}>
        <div className={styles.textCol}>
          {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
          {title ? <h2 className={styles.title}>{title}</h2> : null}
          {hasLead ? (
            <HtmlText html={lead} className={styles.lead} />
          ) : null}
          {hasCta ? (
            ctaHref !== null ? (
              <Link href={ctaHref} className={styles.ctaBtn}>
                {ctaLabel}
              </Link>
            ) : (
              <a href={ctaUrl} className={styles.ctaBtn}>
                {ctaLabel}
              </a>
            )
          ) : null}
        </div>

        {hasItems ? (
          <div className={styles.grid}>
            {items.map((it, idx) => {
              const itemHref = it.href && it.href.trim() !== "" ? extractInternalHref(it.href) : null;
              const cardInner = (
                <>
                  {it.icon ? (
                    <div className={styles.iconBox} aria-hidden="true">
                      <i className={`nc-icon-outline ${it.icon}`} />
                    </div>
                  ) : null}
                  {it.name ? <h3 className={styles.name}>{it.name}</h3> : null}
                </>
              );

              if (it.href && it.href.trim() !== "") {
                return itemHref !== null ? (
                  <Link key={idx} href={itemHref} className={styles.card}>
                    {cardInner}
                  </Link>
                ) : (
                  <a key={idx} href={it.href} className={styles.card}>
                    {cardInner}
                  </a>
                );
              }
              return (
                <div key={idx} className={styles.card}>
                  {cardInner}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}
